/**
 * 从完整 runtime topology 中提取一次引用查询真正需要的身份子集。
 *
 * 除显式请求的面与边外，还保留边的相邻面和端点，因为它们参与 Convert
 * Entities 后续的稳定重定位。返回值不包含未参与本次查询的模型 topology，
 * 避免大型导入模型在 Worker 与主线程之间复制整份拓扑表。
 *
 * @param topology 当前 Worker Shape 的完整 canonical topology。
 * @param faceIndices 本次引用查询直接涉及的面索引。
 * @param edgeIndices 本次引用查询直接涉及的边索引。
 * @returns 可独立提供 Face/Edge/Vertex 身份的 canonical topology 子集。
 */
export function createCanonicalTopologyIdentitySubset(
  topology: RuntimeTopologySnapshot,
  faceIndices: readonly number[],
  edgeIndices: readonly number[],
): RuntimeTopologySnapshot {
  const requestedEdgeIndices = new Set(edgeIndices);
  const edges = topology.edges.filter(edge => requestedEdgeIndices.has(edge.index));
  const requiredFaceIndices = new Set(faceIndices);
  const requiredVertexIndices = new Set<number>();

  edges.forEach((edge) => {
    edge.adjacentFaceIndices.forEach(faceIndex => requiredFaceIndices.add(faceIndex));
    if (edge.vertexAIndex >= 0) {
      requiredVertexIndices.add(edge.vertexAIndex);
    }
    if (edge.vertexBIndex >= 0) {
      requiredVertexIndices.add(edge.vertexBIndex);
    }
  });

  return {
    ...(topology.geometrySource ? {geometrySource: topology.geometrySource} : {}),
    vertices: topology.vertices
      .filter(vertex => requiredVertexIndices.has(vertex.index))
      .map(vertex => structuredClone(vertex)),
    edges: edges.map(edge => structuredClone(edge)),
    faces: topology.faces
      .filter(face => requiredFaceIndices.has(face.index))
      .map(face => structuredClone(face)),
  };
}
